import { PrimaryButton } from "@components/Buttons/PrimaryButton";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useAppSelector } from "src/redux/hooks/redux";
import { selectCurrentCompany } from "src/redux/features/user/selectors/userState.selectors";
import { useCallFlowMutation } from "../../ConfigureCallFlow/hooks/useCallFlowMutation";
import { useCallSettingsQuery } from "../../ConfigureCallFlow/hooks/useCallFlowQuery";
import { DEFAULT_VOICE_MESSAGES } from "../constants/voice-messages.defaults";
import { DEFAULT_TTS } from "../constants/twilio-voice-catalog";
import { CallFlowSettings, VoiceMessagesRecordings, VoiceMessagesSettings as VoiceMessagesSettingsType } from "@components/virtual-receptionist/sms/types/update-company-config";
import { TwilioVoiceSelector, TtsSelection } from "./TwilioVoiceSelector";
import { VoiceMessagePipelineRow } from "./VoiceMessagePipelineRow";
import { VoiceMessageSlot } from "../constants/voice-message-slots";
import { useVoiceMessageRecordingMutation } from "../hooks/useVoiceMessageRecordingMutation";

const ORANGE = "#FB8E00";

type VoiceMessageFormState = {
  noAgentsAvailable: string;
  queueWaiting: string;
  queueConnecting: string;
  recordingNotice: string;
  voicemailGreeting: string;
};

type PipelineMode = "text" | "recording" | "upload";

type PipelineModeState = Record<keyof VoiceMessageFormState, PipelineMode>;

const SLOT_KEY_BY_FIELD: Record<keyof VoiceMessageFormState, VoiceMessageSlot> = {
  noAgentsAvailable: VoiceMessageSlot.CallNotConnected,
  queueWaiting: VoiceMessageSlot.QueueWaiting,
  queueConnecting: VoiceMessageSlot.QueueConnecting,
  recordingNotice: VoiceMessageSlot.CallRecordingNotice,
  voicemailGreeting: VoiceMessageSlot.VoicemailGreeting,
};

const emptyFormState = (): VoiceMessageFormState => ({
  noAgentsAvailable: "",
  queueWaiting: "",
  queueConnecting: "",
  recordingNotice: "",
  voicemailGreeting: "",
});

const defaultPipelineModes = (): PipelineModeState => ({
  noAgentsAvailable: "text",
  queueWaiting: "text",
  queueConnecting: "text",
  recordingNotice: "text",
  voicemailGreeting: "text",
});

const pipelineModesFromRecordings = (recordings?: VoiceMessagesRecordings | null): PipelineModeState => ({
  noAgentsAvailable: recordings?.noAgentsAvailable?.mode === "recording" ? "recording" : "text",
  queueWaiting: recordings?.queueWaiting?.mode === "recording" ? "recording" : "text",
  queueConnecting: recordings?.queueConnecting?.mode === "recording" ? "recording" : "text",
  recordingNotice: recordings?.recordingNotice?.mode === "recording" ? "recording" : "text",
  voicemailGreeting: recordings?.voicemailGreeting?.mode === "recording" ? "recording" : "text",
});

const defaultTts = (): TtsSelection => ({
  language: DEFAULT_TTS.language,
  voice: DEFAULT_TTS.voice,
});

const formStateFromSettings = (voiceMessages?: VoiceMessagesSettingsType | null): VoiceMessageFormState => ({
  noAgentsAvailable: voiceMessages?.call?.noAgentsAvailable ?? "",
  queueWaiting: voiceMessages?.queue?.waiting ?? "",
  queueConnecting: voiceMessages?.queue?.connecting ?? "",
  recordingNotice: voiceMessages?.recording?.recordingNotice ?? "",
  voicemailGreeting: voiceMessages?.voicemail?.greeting ?? "",
});

const ttsFromSettings = (voiceMessages?: VoiceMessagesSettingsType | null): TtsSelection => ({
  language: voiceMessages?.tts?.language ?? DEFAULT_TTS.language,
  voice: voiceMessages?.tts?.voice ?? DEFAULT_TTS.voice,
});

const buildVoiceMessagesPayload = (
  form: VoiceMessageFormState,
  tts: TtsSelection,
): VoiceMessagesSettingsType | null => {
  const voiceMessages: VoiceMessagesSettingsType = {
    tts: {
      language: tts.language,
      voice: tts.voice,
    },
  };

  const queue: NonNullable<VoiceMessagesSettingsType["queue"]> = {};
  const call: NonNullable<VoiceMessagesSettingsType["call"]> = {};
  const recording: NonNullable<VoiceMessagesSettingsType["recording"]> = {};
  const voicemail: NonNullable<VoiceMessagesSettingsType["voicemail"]> = {};

  const noAgentsAvailable = form.noAgentsAvailable.trim();
  const queueWaiting = form.queueWaiting.trim();
  const queueConnecting = form.queueConnecting.trim();
  const recordingNotice = form.recordingNotice.trim();
  const voicemailGreeting = form.voicemailGreeting.trim();

  if (noAgentsAvailable) call.noAgentsAvailable = noAgentsAvailable;
  if (queueWaiting) queue.waiting = queueWaiting;
  if (queueConnecting) queue.connecting = queueConnecting;
  if (recordingNotice) recording.recordingNotice = recordingNotice;
  if (voicemailGreeting) voicemail.greeting = voicemailGreeting;

  if (Object.keys(call).length > 0) voiceMessages.call = call;
  if (Object.keys(queue).length > 0) voiceMessages.queue = queue;
  if (Object.keys(recording).length > 0) voiceMessages.recording = recording;
  if (Object.keys(voicemail).length > 0) voiceMessages.voicemail = voicemail;

  const hasCustomMessages = Boolean(
    voiceMessages.call || voiceMessages.queue || voiceMessages.recording || voiceMessages.voicemail,
  );
  const hasCustomTts =
    tts.language !== DEFAULT_TTS.language || tts.voice !== DEFAULT_TTS.voice;

  if (!hasCustomMessages && !hasCustomTts) {
    return null;
  }

  return voiceMessages;
};

const mergeSettings = (
  existing: CallFlowSettings,
  voiceMessages: VoiceMessagesSettingsType | null,
): CallFlowSettings => {
  const base = { ...(existing ?? {}) };

  if (voiceMessages === null) {
    return {
      ...base,
      voiceMessages: null,
    };
  }

  return {
    ...base,
    voiceMessages,
  };
};

export function VoiceMessagesSettings() {
  const company = useAppSelector(selectCurrentCompany);
  const companyId = company?.id;
  const { data: callFlow, isLoading } = useCallSettingsQuery(companyId!);
  const { update } = useCallFlowMutation();
  const { remove: removeRecording } = useVoiceMessageRecordingMutation();

  const [form, setForm] = useState<VoiceMessageFormState>(emptyFormState);
  const [selectedTts, setSelectedTts] = useState<TtsSelection>(defaultTts);
  const [pipelineModes, setPipelineModes] = useState<PipelineModeState>(defaultPipelineModes);

  const recordings = callFlow?.settings?.voiceMessages?.recordings;

  useEffect(() => {
    setForm(formStateFromSettings(callFlow?.settings?.voiceMessages));
    setSelectedTts(ttsFromSettings(callFlow?.settings?.voiceMessages));
    setPipelineModes(pipelineModesFromRecordings(recordings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callFlow?.settings?.voiceMessages]);

  const handleChange = (field: keyof VoiceMessageFormState) => (value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleModeChange = (field: keyof VoiceMessageFormState) => (mode: PipelineMode) => {
    setPipelineModes((current) => ({ ...current, [field]: mode }));
  };

  const handleSave = async () => {
    if (!companyId) return;

    const fieldsSwitchedBackToText = (Object.keys(pipelineModes) as (keyof VoiceMessageFormState)[]).filter(
      (field) => pipelineModes[field] === "text" && recordings?.[field]?.mode === "recording",
    );

    await Promise.all(
      fieldsSwitchedBackToText.map((field) =>
        removeRecording.mutateAsync({ companyId, slotKey: SLOT_KEY_BY_FIELD[field] }),
      ),
    );

    const voiceMessages = buildVoiceMessagesPayload(form, selectedTts);

    await update.mutateAsync({
      companyId,
      payload: {
        settings: mergeSettings(callFlow?.settings ?? null, voiceMessages),
      },
    });
  };

  const handleRestoreDefaults = async () => {
    if (!companyId) return;

    const fieldsWithActiveRecording = (Object.keys(pipelineModes) as (keyof VoiceMessageFormState)[]).filter(
      (field) => recordings?.[field]?.mode === "recording",
    );

    await Promise.all(
      fieldsWithActiveRecording.map((field) =>
        removeRecording.mutateAsync({ companyId, slotKey: SLOT_KEY_BY_FIELD[field] }),
      ),
    );

    setForm(emptyFormState());
    setSelectedTts(defaultTts());
    setPipelineModes(defaultPipelineModes());

    await update.mutateAsync({
      companyId,
      payload: {
        settings: mergeSettings(callFlow?.settings ?? null, null),
      },
    });
  };

  if (isLoading || !companyId) {
    return null;
  }

  return (
    <Box sx={{ px: 3, pb: 2 }}>
      <Box
        sx={{
          borderRadius: "14px",
          border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
          bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.015)",
          p: 2.2,
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 900,
            color: "var(--fx-surface-text, #0F172A)",
            mb: 0.5,
          }}
        >
          Voice call messages
        </Typography>

        <Typography
          sx={{
            fontSize: 13,
            color: "var(--fx-surface-textsecondary, #64748B)",
            fontWeight: 500,
            mb: 2,
            lineHeight: 1.4,
          }}
        >
          Customize the spoken messages callers hear during queue wait, connection, call recording, and voicemail.
        </Typography>

        <Stack spacing={2}>
          <TwilioVoiceSelector value={selectedTts} onChange={setSelectedTts} />

          <Divider />

          <VoiceMessagePipelineRow
            slotKey={SLOT_KEY_BY_FIELD.noAgentsAvailable}
            label="Call could not connect message"
            placeholder={DEFAULT_VOICE_MESSAGES.call.noAgentsAvailable}
            helperText="Played when the call cannot be connected: no agents available, no answer, busy, failed, or canceled."
            textValue={form.noAgentsAvailable}
            onTextChange={handleChange("noAgentsAvailable")}
            mode={pipelineModes.noAgentsAvailable}
            onModeChange={handleModeChange("noAgentsAvailable")}
            recordingSlot={recordings?.noAgentsAvailable}
            companyId={companyId}
            minRows={2}
          />

          <VoiceMessagePipelineRow
            slotKey={SLOT_KEY_BY_FIELD.queueWaiting}
            label="Queue wait message"
            placeholder={DEFAULT_VOICE_MESSAGES.queue.waiting}
            helperText="Played while callers wait for an available agent."
            textValue={form.queueWaiting}
            onTextChange={handleChange("queueWaiting")}
            mode={pipelineModes.queueWaiting}
            onModeChange={handleModeChange("queueWaiting")}
            recordingSlot={recordings?.queueWaiting}
            companyId={companyId}
            minRows={2}
          />

          <VoiceMessagePipelineRow
            slotKey={SLOT_KEY_BY_FIELD.queueConnecting}
            label="Queue connecting message"
            placeholder={DEFAULT_VOICE_MESSAGES.queue.connecting}
            helperText="Played when an agent becomes available and the call is being connected."
            textValue={form.queueConnecting}
            onTextChange={handleChange("queueConnecting")}
            mode={pipelineModes.queueConnecting}
            onModeChange={handleModeChange("queueConnecting")}
            recordingSlot={recordings?.queueConnecting}
            companyId={companyId}
            minRows={2}
          />

          <VoiceMessagePipelineRow
            slotKey={SLOT_KEY_BY_FIELD.recordingNotice}
            label="Call recording notice"
            placeholder={DEFAULT_VOICE_MESSAGES.recording.recordingNotice}
            helperText="Played before the call is recorded, when recording is enabled."
            textValue={form.recordingNotice}
            onTextChange={handleChange("recordingNotice")}
            mode={pipelineModes.recordingNotice}
            onModeChange={handleModeChange("recordingNotice")}
            recordingSlot={recordings?.recordingNotice}
            companyId={companyId}
            minRows={2}
          />

          <VoiceMessagePipelineRow
            slotKey={SLOT_KEY_BY_FIELD.voicemailGreeting}
            label="Voicemail greeting"
            placeholder={DEFAULT_VOICE_MESSAGES.voicemail.greeting}
            helperText="Played when callers are sent to voicemail."
            textValue={form.voicemailGreeting}
            onTextChange={handleChange("voicemailGreeting")}
            mode={pipelineModes.voicemailGreeting}
            onModeChange={handleModeChange("voicemailGreeting")}
            recordingSlot={recordings?.voicemailGreeting}
            companyId={companyId}
            minRows={3}
          />
        </Stack>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mt: 2.2,
          }}
        >
          <PrimaryButton
            variant="outlined"
            onClick={handleRestoreDefaults}
            disabled={update.isPending}
            sx={{ borderRadius: "8px" }}
          >
            Restore defaults
          </PrimaryButton>

          <PrimaryButton
            variant="contained"
            onClick={handleSave}
            disabled={update.isPending}
            sx={{ borderRadius: "8px", bgcolor: ORANGE, "&:hover": { bgcolor: "#e07f00" } }}
          >
            Save messages
          </PrimaryButton>
        </Box>
      </Box>
    </Box>
  );
}
